import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Grid, MenuItem } from '@mui/material';
import { fetchClients, fetchProjects, createProject } from '../services/api';
import { useNavigate } from 'react-router-dom';

const AddProject = () => {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    clientId: '',
    projectName: '',
    startDate: '',
    endDate: '',
  });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientList, projectList] = await Promise.all([
          fetchClients(),
          fetchProjects()
        ]);
        setClients(clientList);
        
        // Flatten the grouped projects structure for easier handling
        const flattenedProjects = projectList.flatMap(clientGroup => 
          clientGroup.projects.map(project => ({
            ...project,
            client_name: clientGroup.client_name
          }))
        );
        setProjects(flattenedProjects);
      } catch (error) {
        alert('Failed to load clients or projects');
      }
    };
    loadData();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleReset = () => {
    setFormData({ clientId: '', projectName: '', startDate: '', endDate: '' });
    setError('');
  };

  const isDuplicate = () => {
    return projects.some(
      (p) => String(p.client_id) === String(formData.clientId) && p.project_name.trim().toLowerCase() === formData.projectName.trim().toLowerCase()
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDuplicate()) {
      setError('A project with this name already exists for the selected client.');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      setError('Please provide both start and end dates.');
      return;
    }
    if (formData.startDate >= formData.endDate) {
      setError('Start date must be before end date.');
      return;
    }
    try {
      await createProject({
        clientId: formData.clientId,
        projectName: formData.projectName,
        startDate: formData.startDate,
        endDate: formData.endDate,
      });
      alert('Project saved!');
      navigate('/project/list', { replace: true });
    } catch (error) {
      setError(error?.message || 'Failed to save project.');
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Add New Project
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Select Client"
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                required
              >
                {clients.length === 0 ? (
                  <MenuItem value="">No clients found</MenuItem>
                ) : (
                  clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.company_name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
              <Button type="button" variant="contained" color="warning" onClick={handleReset}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AddProject; 